import MalKort from '../components/MalKort';
import SportcenterNorderi from './SportcenterNorderi';
import SportcenterProdukter, { useSportcenterProdukter, aggregera, periodSpann, PeriodValjare } from './SportcenterProdukter';
import React, { useState, useMemo } from 'react';
import { Clock, SubNav, Metric, KpiRow, DagligForsaljning, DagligSnabb, PlaneringTab, YoYUtforskare, VisaFlerRad, TrendCompare, DagVeckoVy } from '../components/SharedComponents';
import { tkr, fmtMonth, raLabel, raStartYear } from '../utils';

const SPORT_COLOR = '#1A3D2B';
const FJOL_COLOR  = '#A33D5B';

// ── SÄSONG ──────────────────────────────────────────────────────────
// Sportcenter kör nov–apr och står stilla resten av året. Räknat per
// kalendermånad blir blocket obegripligt i lågsäsong: "senaste månaden med
// intäkt" landar på en brusmånad om några tusen kronor, och YTD för ett
// räkenskapsår som nyss börjat säger ingenting alls. Säsongen — räkenskapsåret
// jul–jun — är den enhet verksamheten faktiskt mäts i, så den styr blocket.
const BRUSGOLV  = 50000; // kr/mån — under detta är det efterspel, inte drift
const MAN_KORT  = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];
const RA_ORDNING = [7, 8, 9, 10, 11, 12, 1, 2, 3, 4, 5, 6];

// ÅrMånad (202512) → räkenskapsårets startår (2025)
const raAv   = (ym) => (ym % 100 >= 7 ? Math.floor(ym / 100) : Math.floor(ym / 100) - 1);
const raNamn = (ar) => `${String(ar % 100).padStart(2, '0')}/${String((ar + 1) % 100).padStart(2, '0')}`;

// Månadsraden bär hela koncernen i [6]. Är den noll finns ingen data alls för
// månaden — vilket är något helt annat än att Sportcenter var stängt. Utan den
// skillnaden ser fjolårets saknade höst (data börjar dec 2024) ut som ett ras.
const harData = (r) => !!r && (r[6] || 0) > 0;

const btnStyle = (active) => ({
  background: active ? SPORT_COLOR : 'transparent',
  color: active ? '#fff' : 'var(--text-secondary)',
  border: '1px solid var(--border)', borderRadius: 20,
  padding: '3px 10px', fontSize: 11, cursor: 'pointer',
  fontFamily: 'system-ui,sans-serif', transition: 'all 0.15s',
});

function SportcenterTopProdukt() {
  // Perioderna går hela vägen till säsong och hela historiken — "senaste 3
  // månaderna" är i augusti bara löparkläder, och frågan personalen ställer
  // är hur många skidor som gick under vintern.
  const [period,   setPeriod]   = useState('sasong');
  const [from,     setFrom]     = useState('');
  const [to,       setTo]       = useState('');
  const [sortBy,   setSortBy]   = useState('intakt'); // 'intakt' | 'antal'
  const [visaAlla, setVisaAlla] = useState(false);
  const produkter = useSportcenterProdukter();
  const loading   = produkter === null;

  const eget  = !!(from && to && from <= to);
  const spann = eget ? { from, to } : periodSpann(period);
  const raw   = aggregera(produkter, spann.from, spann.to);
  const full  = [...raw].sort((a, b) => b[sortBy] - a[sortBy]);
  const items  = visaAlla ? full : full.slice(0, 7);
  const maxVal = Math.max(...items.map(i => i[sortBy] || 0), 1);

  const valLabel = (item) =>
    sortBy === 'antal' ? `${item.antal} st` : `${Math.round(item.intakt).toLocaleString('sv-SE')} kr`;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div className="card-title" style={{ marginBottom: 0 }}>Populäraste produkter</div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setSortBy('intakt')} style={btnStyle(sortBy === 'intakt')}>Intäkt</button>
          <button onClick={() => setSortBy('antal')}  style={btnStyle(sortBy === 'antal')}>Antal</button>
        </div>
      </div>
      <PeriodValjare vald={period} setVald={setPeriod} from={from} setFrom={setFrom} to={to} setTo={setTo} />
      {loading && <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Laddar...</div>}
      {!loading && items.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: 'system-ui,sans-serif' }}>Ingen data för perioden</div>
      )}
      {!loading && items.map((item, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 6 }}>
          <div style={{ width: 14, fontSize: 10, color: '#bbb', textAlign: 'right', fontFamily: 'system-ui,sans-serif', flexShrink: 0 }}>{i + 1}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
              <span style={{ fontSize: 11, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', fontFamily: 'system-ui,sans-serif', flexShrink: 0, marginLeft: 8 }}>{valLabel(item)}</span>
            </div>
            <div style={{ height: 3, background: 'var(--panel2)', borderRadius: 2 }}>
              <div style={{ height: '100%', width: `${Math.round(item[sortBy] / maxVal * 100)}%`, background: SPORT_COLOR, borderRadius: 2 }} />
            </div>
          </div>
        </div>
      ))}
      {!loading && <VisaFlerRad total={full.length} expanded={visaAlla} onToggle={() => setVisaAlla(v => !v)} />}
      <div className="note" style={{ marginTop: 10 }}>Källa: Qlik · Zettle-transaktioner Sportcenter</div>
    </div>
  );
}

// Månadsrader → en post per räkenskapsår, med månaderna kvar var för sig.
// Summorna räknar bara månader som har data; tomma rader ska inte dra ner
// varken TB1 eller timmar.
export function byggSasonger(rows) {
  const per = new Map();
  for (const r of rows || []) {
    const ym = Number(r[0]);
    if (!/^\d{6}$/.test(String(ym))) continue;
    const ar = raAv(ym);
    if (!per.has(ar)) per.set(ar, { ar, rev: 0, tb1: 0, tim: 0, lon: 0, manad: new Map() });
    const s = per.get(ar);
    s.manad.set(ym % 100, r);
    if (!harData(r)) continue;
    s.rev += r[4]  || 0;
    s.tb1 += r[40] || 0;
    s.tim += r[20] || 0;
    s.lon += Math.abs(r[47] || 0);
  }
  return [...per.values()].sort((a, b) => b.ar - a.ar);
}

// Säsongens tolv månader i räkenskapsårsordning, med fjolåret bredvid.
// Saknad månad blir null och inte noll — TrendCompare ritar glapp i stället
// för att dra kurvan i botten, och jämförelsen nedan hoppar över den.
export function byggKurva(s, f) {
  return RA_ORDNING.map((mm) => {
    const c = s?.manad.get(mm);
    const p = f?.manad.get(mm);
    return {
      label: MAN_KORT[mm],
      value: harData(c) ? (c[4] || 0) : null,
      prev:  harData(p) ? (p[4] || 0) : null,
    };
  });
}

// Bara månader där BÅDA säsongerna har siffror får ingå. Datan börjar dec 2024,
// så säsong 25/26 mot 24/25 rakt av hade visat +13 % tillväxt som inte finns —
// hela skillnaden vore fem månader fjolåret aldrig fick vara med på.
export function jamforbart(kurva) {
  const a = kurva.reduce((acc, p) => (p.value == null || p.prev == null ? acc
    : { cur: acc.cur + p.value, fjol: acc.fjol + p.prev, n: acc.n + 1 }), { cur: 0, fjol: 0, n: 0 });
  return { ...a, pct: a.fjol > 0 ? Math.round((a.cur - a.fjol) / a.fjol * 100) : null };
}

// Säsongen som helhet: nyckeltal, kurvan mot förra säsongen och en ärlig
// jämförelsegrund.
function SasongsOversikt({ rows }) {
  const [vald, setVald] = useState(null);
  const sasonger = useMemo(() => byggSasonger(rows), [rows]);

  // Förvalt: senaste säsongen som faktiskt körts. I augusti är innevarande
  // räkenskapsår ett par tusenlappar sommarförsäljning — fel bild att möta
  // någon med, och det man ser först är det man tror att portalen handlar om.
  const forvald = sasonger.find((s) => s.rev >= 500000)?.ar ?? sasonger[0]?.ar ?? null;
  const ar = vald ?? forvald;
  const s  = sasonger.find((x) => x.ar === ar);
  const f  = sasonger.find((x) => x.ar === ar - 1);
  if (!s) return null;

  const kurva  = byggKurva(s, f);
  const jmf    = jamforbart(kurva);
  const jmfPct = jmf.pct;

  const tg    = s.rev > 0 ? Math.round(s.tb1 / s.rev * 100) : 0;
  const omsh  = s.tim > 0 ? Math.round(s.rev / s.tim)       : 0;
  const pers  = s.rev > 0 && s.lon > 0 ? Math.round(s.lon / s.rev * 100) : null;
  const drift = RA_ORDNING.filter((mm) => (s.manad.get(mm)?.[4] || 0) >= BRUSGOLV);
  const pagar = ar === raStartYear();

  return (
    <>
      <div className="grid-4">
        <Metric
          label={`Intäkt säsong ${raNamn(ar)}`}
          value={s.rev > 0 ? tkr(s.rev) : '–'}
          sub={jmfPct !== null ? `${jmfPct >= 0 ? '+' : ''}${jmfPct}% mot ${raNamn(ar - 1)} (jämförbara mån)` : 'Ingen jämförbar säsong'}
          trend={jmfPct === null ? '' : jmfPct >= 0 ? 'up' : 'down'}
        />
        <Metric
          label="Täckningsgrad"
          value={s.rev > 0 ? `${tg}%` : '–'}
          sub={s.tb1 !== 0 ? `TB1 ${tkr(s.tb1)}` : ''}
          trend={s.tb1 < 0 ? 'down' : ''}
        />
        <Metric
          label="OMS/H"
          value={omsh > 0 ? `${omsh.toLocaleString('sv-SE')} kr/h` : '–'}
          sub={s.tim > 0 ? `${Math.round(s.tim).toLocaleString('sv-SE')} tim` : ''}
        />
        <Metric
          label="Personal%"
          value={pers !== null ? `${pers}%` : '–'}
          sub={s.lon > 0 ? `Lön ${tkr(s.lon)}` : 'Lön ÷ intäkt'}
          trend={pers === null ? '' : pers <= 35 ? 'up' : pers >= 55 ? 'down' : ''}
        />
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <div className="card-title" style={{ marginBottom: 0 }}>
            Säsongskurva {raNamn(ar)}
            <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 8, fontSize: 11.5 }}>
              {drift.length > 0 ? `drift ${MAN_KORT[drift[0]]}–${MAN_KORT[drift[drift.length - 1]]}` : 'ingen driftmånad'}
              {pagar ? ' · pågående' : ''}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            {sasonger.filter((x) => x.rev > 0).map((x) => (
              <button key={x.ar} onClick={() => setVald(x.ar)} style={btnStyle(x.ar === ar)}>{raNamn(x.ar)}</button>
            ))}
          </div>
        </div>
        <TrendCompare
          data={kurva}
          formatVal={(v) => tkr(v)}
          curColor={SPORT_COLOR}
          prevColor={FJOL_COLOR}
          curLabel={raNamn(ar)}
          prevLabel={raNamn(ar - 1)}
        />
        <div className="note" style={{ marginTop: 8 }}>
          {jmf.n > 0
            ? `Jämförbara månader (${jmf.n} st): ${tkr(jmf.cur)} mot ${tkr(jmf.fjol)}${jmfPct !== null ? ` · ${jmfPct >= 0 ? '+' : ''}${jmfPct}%` : ''}. Månader där föregående säsong saknar siffror är utelämnade — datan börjar dec 2024.`
            : 'Ingen överlappande månad att jämföra mot föregående säsong.'}
        </div>
      </div>
    </>
  );
}

export default function SportcenterBlock({ data, isAdmin = false }) {
  // Daglig översikt är alltid förstasidan. Blocket öppnade förut på säsongsvyn
  // i lågsäsong, för att slippa möta en vägg av tankstreck — men den som slår
  // upp Sportcenter vill se dagsläget först, tomt eller inte.
  const [sub, setSub] = useState('daglig');
  const rows  = data?.monthly || [];
  const daily = data?.daily   || [];
  const ytd   = data?.ytd     || {};

  const TABS = [
    { id: 'daglig', label: 'Daglig översikt' },
    { id: 'dagvecka', label: 'Dagar & veckor' },
    { id: 'period', label: 'Säsong & trend'  },
    { id: 'planering', label: 'För planering' },
    { id: 'produkter', label: 'Produkter' },
    { id: 'norderi', label: 'Nörderi' },
  ];

  // Senaste månaden med DRIFT — inte bara med någon krona på sig. Utan golvet
  // blir rubrikkortet augusti med några tusen i sommarförsäljning, och TG och
  // OMS/H räknade på brus.
  const activeRow = rows.find(r => (r[4] || 0) >= BRUSGOLV) || rows.find(r => (r[4] || 0) > 0) || rows[0];
  const r0Rev   = activeRow?.[4]  || 0;
  const r0TB1   = activeRow?.[40] || 0;
  const r0Tim   = activeRow?.[20] || 0;
  const r0TG    = r0Rev > 0 ? Math.round(r0TB1 / r0Rev * 100) : 0;
  const r0OmsH  = r0Tim > 0 ? Math.round(r0Rev / r0Tim)       : 0;
  const r0Label = activeRow?.[0] ? fmtMonth(activeRow[0])      : '';

  // Bästa månad (högst intäkt innevarande dataset)
  const bestRow = rows.reduce((best, r) => ((r[4] || 0) > (best?.[4] || 0) ? r : best), null);
  const bestRev   = bestRow?.[4] || 0;
  const bestLabel = bestRow?.[0] ? fmtMonth(bestRow[0]) : '';

  // YTD
  const ytdRev  = ytd.sportcenter    || 0;
  const ytdTB1  = ytd.tb1Sportcenter || 0;
  const ytdTim  = ytd.timSport       || 0;
  const ytdTG   = ytdRev > 0 ? Math.round(ytdTB1 / ytdRev * 100) : 0;
  const ytdOmsH = ytdTim > 0 ? Math.round(ytdRev / ytdTim)       : 0;
  const ytdLon  = Math.abs(ytd.lonSportcenter || 0);
  const ytdPersPct = ytdRev > 0 && ytdLon > 0 ? Math.round(ytdLon / ytdRev * 100) : null;
  // En hel säsong omsätter 6–7 mkr. Ligger räkenskapsåret långt under en halv
  // har det knappt börjat, och kvoterna (TG, Personal%) blir siffror man bara
  // kan bli lurad av — samma spärr som procentkolumnerna i månadstabellen.
  const ytdPctOk = ytdRev >= 500000;

  return (
    <div className="screen">
      <div className="header header-sport">
        <div>
          <div className="header-title">Bruksvallsliden Sportcenter</div>
          <div className="header-name">Uthyrning · Skidor · Butik · Verkstad</div>
        </div>
        <Clock />
      </div>
      <SubNav tabs={TABS} active={sub} onChange={setSub} />
      {sub === 'daglig' && <MalKort enheter={['Sportcenter']} />}

      {/* ── DAGLIG ÖVERSIKT ── */}
      {sub === 'daglig' && (
        <>
          <DagligSnabb daily={daily} enhet="sportcenter" />
          <div className="grid-3">
            <Metric
              label={r0Label ? `Intäkt ${r0Label}` : 'Intäkt senaste månaden'}
              value={r0Rev > 0 ? tkr(r0Rev) : '–'}
              sub={r0TB1 !== 0 ? `TB1 ${tkr(r0TB1)} · TG ${r0TG}%` : ''}
              trend={r0TB1 > 0 ? 'up' : (r0TB1 < 0 ? 'down' : '')}
            />
            <Metric
              label="OMS/H"
              value={r0OmsH > 0 ? `${r0OmsH.toLocaleString('sv-SE')} kr/h` : '–'}
              sub={r0Tim > 0 ? `${Math.round(r0Tim)} tim` : ''}
            />
            <Metric
              label="Bästa månad"
              value={bestRev > 0 ? `${bestLabel} ${tkr(bestRev)}` : '–'}
              sub="Vinter toppmånad"
              trend="up"
            />
          </div>
          <div className="grid-2">
            <div className="card">
              <div className="card-title">Månadsintäkt Sportcenter</div>
              {rows.slice(0, 6).map((r, i) => {
                const rev = r[4] || 0;
                return (
                  <KpiRow
                    key={i}
                    label={fmtMonth(r[0])}
                    value={rev > 0 ? tkr(rev) : '–'}
                    badge={i === 0 ? { type: 'blue', text: 'Senaste' } : null}
                  />
                );
              })}
              <div className="note">Källa: Qlik — Resultatenhet Sportcenter</div>
            </div>
            <SportcenterTopProdukt />
          </div>
          <DagligForsaljning daily={daily} enhet="sportcenter" />
        </>
      )}

      {/* ── PERIOD & TREND ── */}
      {/* ── DAGAR & VECKOR ── */}
      {sub === 'dagvecka' && <DagVeckoVy enheter={['Sportcenter']} color={SPORT_COLOR} />}

      {sub === 'period' && (
        <>
          <SasongsOversikt rows={rows} />

          <div className="grid-4">
            <Metric
              label={`Sportcenter ${raLabel()} hittills`}
              value={ytdRev > 0 ? tkr(ytdRev) : '–'}
              sub={ytdTB1 !== 0 ? `TB1 ${tkr(ytdTB1)}${ytdPctOk ? ` · TG ${ytdTG}%` : ''}` : ''}
              trend={ytdTB1 > 0 ? 'up' : (ytdTB1 < 0 ? 'down' : '')}
            />
            <Metric
              label="Täckningsgrad"
              value={ytdPctOk ? `${ytdTG}%` : '–'}
              sub={ytdPctOk ? `TB1 ${tkr(ytdTB1)}` : 'För lite drift att räkna på'}
              trend={ytdPctOk && ytdTB1 < 0 ? 'down' : ''}
            />
            <Metric
              label="OMS/H"
              value={ytdOmsH > 0 ? `${ytdOmsH.toLocaleString('sv-SE')} kr/h` : '–'}
              sub={ytdTim > 0 ? `${Math.round(ytdTim).toLocaleString('sv-SE')} tim` : ''}
            />
            <Metric
              label="Personal%"
              value={ytdPctOk && ytdPersPct !== null ? `${ytdPersPct}%` : '–'}
              sub={ytdLon > 0 ? `Lön ${tkr(ytdLon)}` : 'Lön ÷ intäkt'}
              trend={!ytdPctOk || ytdPersPct === null ? '' : ytdPersPct <= 35 ? 'up' : ytdPersPct >= 55 ? 'down' : ''}
            />
          </div>
          <div className="card">
            <div className="card-title">Månadsöversikt Sportcenter</div>
            <div className="period-table">
              <div className="period-header period-row-7">
                <span>Månad</span>
                <span>Intäkt</span>
                <span>TB1</span>
                <span>TG%</span>
                <span>Tim</span>
                <span>OMS/H</span>
                <span>Pers%</span>
              </div>
              {rows.map((r, i) => {
                const rev  = r[4]  || 0;
                const tb1  = r[40] || 0;
                const tim  = r[20] || 0;
                const lon  = r[47] || 0;
                // Samma månad ifjol (ÅrMånad − 100) för YoY-coaching
                const fRev = rows.find(x => Number(x[0]) === Number(r[0]) - 100)?.[4] || 0;
                // Procenttal döljs för månader med försumbar intäkt (<50 tkr)
                const pctOk = rev >= 50000;
                const tg   = rev > 0 ? Math.round(tb1 / rev * 100) : 0;
                const omsh = tim > 0 ? Math.round(rev / tim)       : 0;
                const pers = rev > 0 ? Math.round(Math.abs(lon) / rev * 100) : 0;
                return (
                  <div key={i} className="period-row period-row-7">
                    <span>{fmtMonth(r[0])}</span>
                    <span>
                      {rev > 0 ? tkr(rev) : '–'}
                      {fRev > 0 && (
                        <div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>
                          ifjol {tkr(fRev)}{rev > 0 && <span style={{ fontWeight: 600, color: rev >= fRev ? '#3B6D11' : '#A32D2D' }}> · {rev >= fRev ? '+' : ''}{Math.round((rev - fRev) / fRev * 100)}%</span>}
                        </div>
                      )}
                    </span>
                    <span>{tb1 !== 0 ? tkr(tb1) : '–'}</span>
                    <span>{pctOk ? `${tg}%`  : '–'}</span>
                    <span>{tim  > 0 ? Math.round(tim) : '–'}</span>
                    <span>{omsh > 0 ? `${omsh.toLocaleString('sv-SE')} kr` : '–'}</span>
                    <span>{pctOk && lon !== 0 ? `${pers}%` : '–'}</span>
                  </div>
                );
              })}
            </div>
            <div className="note">Källa: Qlik — Resultatenhet Sportcenter</div>
          </div>

          {/* YoY-utforskaren — gräv i utvecklingen mot ifjol */}
          <YoYUtforskare enheter={['Sportcenter']} hoursEnhet="Sportcenter" color={SPORT_COLOR} />
        </>
      )}

      {/* ── FÖR PLANERING ── */}
      {/* ── PRODUKTER ── */}
      {sub === 'produkter' && <SportcenterProdukter />}

      {/* ── NÖRDERI ── */}
      {sub === 'norderi' && <SportcenterNorderi rows={rows} isAdmin={isAdmin} />}

      {sub === 'planering' && (
        <PlaneringTab enheter={['Sportcenter']} color="#1A3D2B" />
      )}
    </div>
  );
}
